// MAJLIS-37, the room lock (D-30, D-15, UI-C14; UI-C30..C32 PROPOSED ROWS), drawn as the owner-approved mock-up
// (docs/spec/prototype/room-lock-mockup.html, 2026-09-24): the host's knock cards under the header (at most two, the
// rest behind "+N more waiting"), the seat sheet's "Waiting to join (n)" list, and the knocker's waiting screen.
// MAJLIS-173 (component-rules.md section 1, the TONAL kind; the owner-approved screen-rules mock-up of 2026-10-05, frame
// (k)): the yes is a TONAL button (the accent tint - a lesser yes, so the strip stays quiet beside the mic, the screen's
// one big blue control) and the no a MUTED text link, in the strip and in the People sheet's waiting list alike - one
// pattern for one decision. The words stay the catalogue's own Approve / Deny (the People sheet's existing strings, in
// all 30 languages; no new key). Approve comes first in DOM order, so it reads first in LTR and RTL alike. "N more
// waiting" is a text link that opens the People sheet.
import { useState } from "react";
import { languageName } from "../data/languages";
import { useI18n } from "../i18n";
import type { Knock } from "../protocol/types";
import { initialOf } from "./Seat";

/** ASSUMPTION (the mock-up's 3b/3c): at most two knock cards under the header, so the canvas keeps its space; with
 * three or more, the first card and "+N more waiting". */
export const KNOCK_CARDS_MAX = 2;

export const LockIcon = () => (
  <svg
    className="icon"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

function KnockRow({
  knock,
  inSheet,
  onAnswer,
}: {
  knock: Knock;
  inSheet?: boolean;
  onAnswer: (identityId: string, admit: boolean) => void;
}) {
  const { lang, t } = useI18n();
  const speaks = t("speaks", { lang: languageName(knock.speak_lang, lang) });
  return (
    <div
      className={inSheet ? "knock-row in-sheet" : "knock-row"}
      role="group"
      aria-label={t("knockSay", { name: knock.name, lang: languageName(knock.speak_lang, lang) })}
      data-spec="UI-C31 UI-C14 D-30 D-15"
    >
      <span className="seat-dot knock-dot" aria-hidden="true">
        {initialOf(knock.name)}
      </span>
      <span className="knock-text">
        <span className="knock-line">
          <bdi className="knock-name">{knock.name}</bdi> <span className="knock-wants">{t("knockWants")}</span>
        </span>
        <span className="knock-meta">{knock.kind === "shared" ? `${t("sharedSeat")} · ${speaks}` : speaks}</span>
      </span>
      <span className="knock-actions">
        <button
          type="button"
          className="tonal knock-approve"
          aria-label={t("approveNamed", { name: knock.name })}
          onClick={() => onAnswer(knock.identity_id, true)}
          data-spec="UI-C31 P-25"
        >
          {t("approve")}
        </button>
        <button
          type="button"
          className="text-link muted knock-deny"
          aria-label={t("denyNamed", { name: knock.name })}
          onClick={() => onAnswer(knock.identity_id, false)}
          data-spec="UI-C31 P-25"
        >
          {t("deny")}
        </button>
      </span>
    </div>
  );
}

/** UI-C31 (the mock-up's 3a-3c): host only, directly under the header and OUTSIDE the one banner slot (status stays
 * status; this needs an answer). Shows only the server's list: a card leaves when the next `knocks` says so. Each NEW
 * knock is announced once, politely. */
export function KnockCards({
  knocks,
  onAnswer,
  onMore,
}: {
  knocks: Knock[];
  onAnswer: (identityId: string, admit: boolean) => void;
  onMore: () => void;
}) {
  const { lang, t } = useI18n();
  const newest = knocks[knocks.length - 1];
  // the knocks already announced, as the ids of the last list seen; a knock not in it is new and is said once
  const ids = knocks.map((k) => k.identity_id).join("\n");
  const [seenIds, setSeenIds] = useState<string | null>(null);
  const [said, setSaid] = useState("");
  if (ids !== seenIds) {
    const before = new Set(seenIds === null ? [] : seenIds.split("\n"));
    const fresh = knocks.filter((k) => !before.has(k.identity_id));
    setSeenIds(ids);
    if (fresh.length > 0) {
      const k = fresh[fresh.length - 1];
      setSaid(t("knockSay", { name: k.name, lang: languageName(k.speak_lang, lang) }));
    }
  }
  const shown = knocks.length > KNOCK_CARDS_MAX ? knocks.slice(0, KNOCK_CARDS_MAX - 1) : knocks;
  const more = knocks.length - shown.length;
  return (
    <>
      {newest && (
        <section
          className="knock-stack"
          aria-label={t("waitingToJoin", { n: knocks.length })}
          data-spec="UI-C31 UI-C14 D-30"
        >
          {shown.map((k) => (
            <KnockRow key={k.identity_id} knock={k} onAnswer={onAnswer} />
          ))}
          {more > 0 && (
            <button type="button" className="text-link knock-more" onClick={onMore} data-spec="UI-C31 UI-C10">
              {t("moreWaiting", { n: more })}
            </button>
          )}
        </section>
      )}
      <p className="visually-hidden" aria-live="polite" data-testid="knock-say">
        {said}
      </p>
    </>
  );
}

/** UI-C31 (3c): the seat sheet's host-only "Waiting to join (n)" section, ABOVE the seat list: every knock. */
export function WaitingList({
  knocks,
  onAnswer,
}: {
  knocks: Knock[];
  onAnswer: (identityId: string, admit: boolean) => void;
}) {
  const { t } = useI18n();
  if (knocks.length === 0) return null;
  return (
    <section className="host-section waiting-section" data-spec="UI-C31 UI-C14 D-30">
      <p className="host-title">{t("waitingToJoin", { n: knocks.length })}</p>
      {knocks.map((k) => (
        <KnockRow key={k.identity_id} knock={k} inSheet onAnswer={onAnswer} />
      ))}
    </section>
  );
}

/** UI-C32 (the mock-up's 2a and 4c): the knocker's screen, laid out as the ending screen. It shows the code they typed
 * and what the host sees about them - nothing about the room. role=status: the title is read once; the dots are
 * decoration. Cancel withdraws the knock (the socket closes) and returns to the entry form with the fields kept. */
export function WaitingScreen({
  code,
  name,
  speakLang,
  hostPresent,
  onCancel,
}: {
  code: string;
  name: string;
  speakLang: string;
  hostPresent: boolean;
  onCancel: () => void;
}) {
  const { lang, t } = useI18n();
  return (
    <main className="ending waiting" role="status" aria-live="polite" data-spec="UI-S1 UI-C32 UI-C14 D-30">
      <span className="wait-lock" aria-hidden="true">
        <LockIcon />
      </span>
      <h1 className="ending-title">{t("waitTitle")}</h1>
      <p className="ending-body">{t("waitBody", { code })}</p>
      <p className="who-card">
        <span className="seat-dot knock-dot" aria-hidden="true">
          {initialOf(name)}
        </span>
        <span>
          <b dir="auto">{name}</b>
          <br />
          <span className="knock-meta">{t("speaks", { lang: languageName(speakLang, lang) })}</span>
        </span>
      </p>
      {!hostPresent && <p className="away-note">{t("hostAway")}</p>}
      <span className="live-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <button type="button" className="text-link" onClick={onCancel} data-spec="UI-C32 UI-C14 D-30">
        {t("cancel")}
      </button>
    </main>
  );
}
